'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import LanguageSwitcher from '@/components/i18n/LanguageSwitcher';
import { Menu, X, LogOut } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { useTranslation } from '@/i18n';
import { usePlatformSettings } from '@/contexts/PlatformSettingsContext';

export default function LandingNav() {
  const { t, locale } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navItems, setNavItems] = useState<
    Array<{ label: string; href: string; item_type: string; opens_new_tab: boolean }>
  >([]);
  const [navLoaded, setNavLoaded] = useState(false);
  const { user, profile, userRole, loading, signOut } = useAuth();
  const pathname = usePathname();
  const { platformName } = usePlatformSettings();

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handler, { passive: true });
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => {
    const fetchNavItems = async () => {
      setNavLoaded(false);
      try {
        try {
          sessionStorage.removeItem('public_nav_items_v1');
        } catch {
          // Ignore storage access issues for public navigation fallback.
        }

        const supabase = createClient();
        const { data, error } = await supabase
          .from('platform_nav_items')
          .select('label, href, item_type, opens_new_tab, sort_order')
          .eq('is_enabled', true)
          .order('sort_order', { ascending: true })
          .limit(20);
        if (error) {
          setNavItems([]);
          setNavLoaded(true);
          return;
        }
        const rows = (data as any[]) || [];
        const mapped = rows
          .map((r) => ({
            label: String(r.label || ''),
            href: String(r.href || ''),
            item_type: String(r.item_type || 'anchor'),
            opens_new_tab: r.opens_new_tab === true,
            sort_order: Number(r.sort_order || 0),
          }))
          .filter((r) => r.label && r.href);
        setNavItems(mapped);
        setNavLoaded(true);
      } catch {
        setNavItems([]);
        setNavLoaded(true);
      }
    };
    fetchNavItems();
  }, []);

  const isSuperAdmin = profile?.role === 'SUPER_ADMIN' || userRole === 'SUPER_ADMIN';

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (_error) {
      // Keep the current screen if sign-out fails.
    }
  };

  const renderHeaderCTA = () => {
    if (!user) {
      return (
        <>
          <Link
            href="/sign-up-login-screen?mode=login"
            className="hidden sm:inline-flex btn-ghost text-sm font-medium whitespace-nowrap"
          >
            {t('publicWebsite:nav.logIn') || (locale === 'ar' ? 'تسجيل الدخول' : 'Log In')}
          </Link>
          <Link
            href="/sign-up-login-screen?mode=signup"
            className="inline-flex btn-primary text-xs sm:text-sm px-3.5 py-2 font-semibold whitespace-nowrap"
          >
            {t('publicWebsite:nav.startFreeTrial') ||
              (locale === 'ar' ? 'ابدأ النسخة التجريبية المجانية' : 'Start Free Trial')}
          </Link>
        </>
      );
    }
    if (loading) {
      return (
        <Link
          href="/tenant-dashboard"
          className="inline-flex btn-primary text-xs sm:text-sm px-3.5 py-2 font-semibold whitespace-nowrap"
        >
          {t('publicWebsite:nav.dashboard') || (locale === 'ar' ? 'لوحة التحكم' : 'Dashboard')}
        </Link>
      );
    }
    if (isSuperAdmin) {
      return (
        <>
          <Link
            href="/super-admin"
            className="inline-flex btn-primary text-xs sm:text-sm px-3.5 py-2 font-semibold whitespace-nowrap"
          >
            {t('publicWebsite:nav.superAdminDashboard') ||
              (locale === 'ar' ? 'لوحة تحكم المدير التنفيذي' : 'Super Admin Dashboard')}
          </Link>
          <button
            onClick={handleSignOut}
            className="hidden sm:inline-flex items-center gap-1.5 btn-ghost text-sm font-medium whitespace-nowrap"
          >
            <LogOut className="w-4 h-4" />
            {t('publicWebsite:nav.logout') || (locale === 'ar' ? 'تسجيل الخروج' : 'Logout')}
          </button>
        </>
      );
    }
    return (
      <>
        <Link
          href="/tenant-dashboard"
          className="inline-flex btn-primary text-xs sm:text-sm px-3.5 py-2 font-semibold whitespace-nowrap"
        >
          {t('publicWebsite:nav.dashboard') || (locale === 'ar' ? 'لوحة التحكم' : 'Dashboard')}
        </Link>
        <button
          onClick={handleSignOut}
          className="hidden sm:inline-flex items-center gap-1.5 btn-ghost text-sm font-medium whitespace-nowrap"
        >
          <LogOut className="w-4 h-4" />
          {t('publicWebsite:nav.logout') || (locale === 'ar' ? 'تسجيل الخروج' : 'Logout')}
        </button>
      </>
    );
  };

  const renderMobileCTA = () => {
    if (!user) {
      return (
        <>
          <Link
            href="/sign-up-login-screen?mode=login"
            onClick={() => setMobileOpen(false)}
            className="sm:hidden btn-outline w-full justify-center text-sm"
          >
            {t('publicWebsite:nav.logIn') || (locale === 'ar' ? 'تسجيل الدخول' : 'Log In')}
          </Link>
          <Link
            href="/sign-up-login-screen?mode=signup"
            onClick={() => setMobileOpen(false)}
            className="btn-accent w-full justify-center text-sm"
          >
            {t('publicWebsite:nav.startFreeTrial') ||
              (locale === 'ar' ? 'ابدأ النسخة التجريبية المجانية' : 'Start Free Trial')}
          </Link>
        </>
      );
    }
    if (loading) {
      return (
        <Link
          href="/tenant-dashboard"
          onClick={() => setMobileOpen(false)}
          className="btn-primary w-full justify-center text-sm"
        >
          {t('publicWebsite:nav.dashboard') || (locale === 'ar' ? 'لوحة التحكم' : 'Dashboard')}
        </Link>
      );
    }
    if (isSuperAdmin) {
      return (
        <>
          <Link
            href="/super-admin"
            onClick={() => setMobileOpen(false)}
            className="btn-primary w-full justify-center text-sm"
          >
            {t('publicWebsite:nav.superAdminDashboard') ||
              (locale === 'ar' ? 'لوحة تحكم المدير التنفيذي' : 'Super Admin Dashboard')}
          </Link>
          <button
            onClick={() => {
              handleSignOut();
              setMobileOpen(false);
            }}
            className="btn-outline w-full justify-center text-sm flex items-center gap-2"
          >
            <LogOut className="w-4 h-4" />{' '}
            {t('publicWebsite:nav.logout') || (locale === 'ar' ? 'تسجيل الخروج' : 'Logout')}
          </button>
        </>
      );
    }
    return (
      <>
        <Link
          href="/tenant-dashboard"
          onClick={() => setMobileOpen(false)}
          className="btn-primary w-full justify-center text-sm"
        >
          {t('publicWebsite:nav.dashboard') || (locale === 'ar' ? 'لوحة التحكم' : 'Dashboard')}
        </Link>
        <button
          onClick={() => {
            handleSignOut();
            setMobileOpen(false);
          }}
          className="btn-outline w-full justify-center text-sm flex items-center gap-2"
        >
          <LogOut className="w-4 h-4" />{' '}
          {t('publicWebsite:nav.logout') || (locale === 'ar' ? 'تسجيل الخروج' : 'Logout')}
        </button>
      </>
    );
  };

  const emergencyFallbackMenu = [
    {
      label: t('publicWebsite:nav.emergencyMenu.home') || (locale === 'ar' ? 'الرئيسية' : 'Home'),
      item_type: 'internal',
      href: '/',
      opens_new_tab: false,
    },
    {
      label:
        t('publicWebsite:nav.emergencyMenu.pricing') || (locale === 'ar' ? 'الأسعار' : 'Pricing'),
      item_type: 'anchor',
      href: '#pricing',
      opens_new_tab: false,
    },
    {
      label:
        t('publicWebsite:nav.emergencyMenu.contact') || (locale === 'ar' ? 'اتصل بنا' : 'Contact'),
      item_type: 'internal',
      href: '/contact',
      opens_new_tab: false,
    },
  ];

  const effectiveNavItems = navLoaded
    ? navItems.length > 0
      ? navItems
      : emergencyFallbackMenu
    : [];

  const resolveHref = (itemType: string, href: string) => {
    if (itemType === 'anchor' && href.startsWith('#')) {
      return pathname === '/' ? href : `/${href}`;
    }
    return href;
  };

  function translateNavLabel(rawLabel: string): string {
    const s = String(rawLabel || '').trim();
    const normalized = s.toLowerCase();
    const KNOWN: Record<string, string> = {
      home: 'publicWebsite:nav.links.home',
      about: 'publicWebsite:nav.links.about',
      'about us': 'publicWebsite:nav.links.about',
      features: 'publicWebsite:nav.links.features',
      pricing: 'publicWebsite:nav.links.pricing',
      blog: 'publicWebsite:nav.links.blog',
      contact: 'publicWebsite:nav.links.contact',
      'contact us': 'publicWebsite:nav.links.contact',
      faqs: 'publicWebsite:nav.links.faqs',
      faq: 'publicWebsite:nav.links.faqs',
      'privacy policy': 'publicWebsite:nav.links.privacyPolicy',
      privacy: 'publicWebsite:nav.links.privacyPolicy',
      'terms of service': 'publicWebsite:nav.links.terms',
      terms: 'publicWebsite:nav.links.terms',
      'data processing agreement': 'publicWebsite:nav.links.dpa',
      dpa: 'publicWebsite:nav.links.dpa',
      'book demo': 'publicWebsite:nav.links.bookDemo',
      demo: 'publicWebsite:nav.links.bookDemo',
      testimonials: 'publicWebsite:nav.links.testimonials',
      'desktop app': 'publicWebsite:nav.links.desktopApp',
      desktop: 'publicWebsite:nav.links.desktopApp',
      'desktop application': 'publicWebsite:nav.links.desktopApp',
    };
    const key = KNOWN[normalized];
    if (!key) return s;
    return t(key) || s;
  }

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-200 bg-white/95 backdrop-blur-md border-b border-border ${
        scrolled ? 'shadow-nav' : ''
      }`}
    >
      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-nowrap items-center justify-between gap-3 h-16">
          {/* Logo */}
          <Link
            href="/"
            className="flex min-w-0 flex-shrink-0 items-center gap-2 whitespace-nowrap"
          >
            <AppLogo className="h-8 w-auto" size={36} />
            <span className="font-bold text-xl tracking-tight text-primary whitespace-nowrap">
              {platformName}
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden xl:flex items-center gap-6 flex-nowrap whitespace-nowrap">
            {navLoaded ? (
              effectiveNavItems.map((item) => {
                const href = resolveHref(item.item_type, item.href);
                const key = `nav-${item.label}-${href}`;
                const className =
                  'text-sm font-medium text-muted-foreground transition-colors hover:text-accent';
                const label = translateNavLabel(item.label);
                if (item.item_type === 'external') {
                  return (
                    <a
                      key={key}
                      href={href}
                      target={item.opens_new_tab ? '_blank' : undefined}
                      rel={item.opens_new_tab ? 'noopener noreferrer' : undefined}
                      className={className}
                    >
                      {label}
                    </a>
                  );
                }
                return (
                  <Link key={key} href={href} className={className}>
                    {label}
                  </Link>
                );
              })
            ) : (
              <>
                <span className="text-sm font-medium opacity-0 select-none">
                  {t('publicWebsite:nav.emergencyMenu.home') ||
                    (locale === 'ar' ? 'الرئيسية' : 'Home')}
                </span>
                <span className="text-sm font-medium opacity-0 select-none">
                  {t('publicWebsite:nav.emergencyMenu.pricing') ||
                    (locale === 'ar' ? 'الأسعار' : 'Pricing')}
                </span>
                <span className="text-sm font-medium opacity-0 select-none">
                  {t('publicWebsite:nav.emergencyMenu.contact') ||
                    (locale === 'ar' ? 'اتصل بنا' : 'Contact')}
                </span>
              </>
            )}
          </nav>

          {/* Right-side actions */}
          <div className="flex items-center justify-end gap-2 sm:gap-3 flex-nowrap whitespace-nowrap flex-shrink-0">
            <Link
              href="/desktop-app"
              className="hidden md:inline-flex whitespace-nowrap text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
            >
              {t('publicWebsite:nav.desktopApp') ||
                (locale === 'ar' ? 'تطبيق سطح المكتب' : 'Desktop App')}
            </Link>
            {renderHeaderCTA()}
            <div className="whitespace-nowrap flex-shrink-0">
              <LanguageSwitcher />
            </div>
            <button
              onClick={() => setMobileOpen((p) => !p)}
              className="xl:hidden flex-shrink-0 p-2 rounded-lg text-muted-foreground transition-colors hover:bg-muted"
              aria-label={
                t('publicWebsite:nav.toggleMobileMenu') ||
                (locale === 'ar' ? 'تبديل قائمة الهاتف' : 'Toggle mobile menu')
              }
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="xl:hidden bg-white border-t border-border py-4 px-2 space-y-1 fade-in">
            {effectiveNavItems.map((item) => {
              const href = resolveHref(item.item_type, item.href);
              const key = `mobile-nav-${item.label}-${href}`;
              const className =
                'block px-4 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors';
              const label = translateNavLabel(item.label);
              if (item.item_type === 'external') {
                return (
                  <a
                    key={key}
                    href={href}
                    target={item.opens_new_tab ? '_blank' : undefined}
                    rel={item.opens_new_tab ? 'noopener noreferrer' : undefined}
                    onClick={() => setMobileOpen(false)}
                    className={className}
                  >
                    {label}
                  </a>
                );
              }
              return (
                <Link
                  key={key}
                  href={href}
                  onClick={() => setMobileOpen(false)}
                  className={className}
                >
                  {label}
                </Link>
              );
            })}
            <Link
              key="mobile-nav-desktop-app"
              href="/desktop-app"
              onClick={() => setMobileOpen(false)}
              className="md:hidden block px-4 py-2.5 rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-colors"
            >
              {t('publicWebsite:nav.desktopApp') ||
                (locale === 'ar' ? 'تطبيق سطح المكتب' : 'Desktop App')}
            </Link>
            <div className="pt-3 border-t border-border flex justify-center py-2">
              <LanguageSwitcher />
            </div>
            <div className="pt-3 border-t border-border flex flex-col gap-2">
              {renderMobileCTA()}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
